import type { Goal } from '../types';

export interface RiskAnalysis {
  topStrength: {
    area: string;
    savePercentage: number;
    totalShots: number;
    goals: number;
  };
  topWeakness: {
    area: string;
    goalPercentage: number;
    totalShots: number;
    goals: number;
  };
  mostRiskySituation: {
    situation: string;
    goalPercentage: number;
    totalShots: number;
    goals: number;
  };
  mostRiskyShotType: {
    shotType: string;
    goalPercentage: number;
    totalShots: number;
    goals: number;
  };
}

// Define goal net areas for analysis (4x4 grid to match heatmap)
// 
// COORDINATE SYSTEM EXPLANATION:
// The goal diagram uses a 4x4 grid system (16 zones) to match the existing
// goal distribution heatmap. Grid lines are at positions 25, 50, 75.
//
// VISUAL LAYOUT (4x4 grid):
//   0 -- 25 -- 50 -- 75 -- 100
//   |  0 |  1 |  2 |  3 |  0
//   |----|----|----|----|
//   |  4 |  5 |  6 |  7 | 25
//   |----|----|----|----|
//   |  8 |  9 | 10 | 11 | 50
//   |----|----|----|----|
//   | 12 | 13 | 14 | 15 | 75
//   |----|----|----|----|
//                        100
//
// Zone naming uses traditional quadrant names for the 4 corners
const GOAL_AREAS = [
  // Top Left quadrant (zones 0,1,4,5)
  { name: 'Top Left', zones: [0, 1, 4, 5] },
  // Top Right quadrant (zones 2,3,6,7)  
  { name: 'Top Right', zones: [2, 3, 6, 7] },
  // Bottom Left quadrant (zones 8,9,12,13)
  { name: 'Bottom Left', zones: [8, 9, 12, 13] },
  // Bottom Right quadrant (zones 10,11,14,15)
  { name: 'Bottom Right', zones: [10, 11, 14, 15] }
];

// Helper function to get zone index from coordinates (matches heatmap logic)
const getZoneIndex = (x: number, y: number): number => {
  const col = Math.floor(x / 25);
  const row = Math.floor(y / 25);
  return row * 4 + col;
};

export class RiskAnalysisService {
  // Helper method to determine which zone a goal position belongs to
  static getZoneForPosition(x: number, y: number): string {
    const zoneIndex = getZoneIndex(x, y);
    for (const area of GOAL_AREAS) {
      if (area.zones.includes(zoneIndex)) {
        return area.name;
      }
    }
    return 'Unknown';
  }

  // Debug method to show zone distribution for troubleshooting
  static debugZoneDistribution(goals: Goal[]): { [zoneName: string]: { goals: Goal[], count: number } } {
    const distribution: { [zoneName: string]: { goals: Goal[], count: number } } = {};
    
    GOAL_AREAS.forEach(area => {
      distribution[area.name] = { goals: [], count: 0 };
    });
    
    goals.forEach(goal => {
      if (typeof goal.xPosition === 'number' && typeof goal.yPosition === 'number') {
        const zone = this.getZoneForPosition(goal.xPosition, goal.yPosition);
        if (distribution[zone]) {
          distribution[zone].goals.push(goal);
          distribution[zone].count++;
        }
      }
    });
    
    return distribution;
  }

  static analyzeRisks(goals: Goal[]): RiskAnalysis | null {
    try {
      // Need more than 1 goal for meaningful analysis
      if (!goals || goals.length <= 1) {
        return null;
      }

      // Use all goals - assume missing result field means it's a goal (since it's in goals collection)
      const validGoals = goals;

      // Calculate weakness first, then strength (excluding weakness area)
      const topWeakness = this.calculateTopWeakness(validGoals);
      const topStrength = this.calculateTopStrength(validGoals, topWeakness.area);
      const mostRiskySituation = this.calculateMostRiskySituation(validGoals);
      const mostRiskyShotType = this.calculateMostRiskyShotType(validGoals);

      return {
        topStrength,
        topWeakness,
        mostRiskySituation,
        mostRiskyShotType
      };
    } catch (error) {
      console.error('Error analyzing risks:', error);
      return null;
    }
  }

  private static calculateTopStrength(goals: Goal[], excludeArea?: string) {
    try {
      const areaStats = this.calculateAreaStats(goals);
      
      if (areaStats.length === 0) {
        return {
          area: 'Top Left',
          savePercentage: 0,
          totalShots: 0,
          goals: 0
        };
      }
    
    // Find area with lowest goal percentage (highest save percentage)
    // Exclude specified area to prevent duplicates
    const availableAreas = excludeArea 
      ? areaStats.filter(area => area.name !== excludeArea)
      : areaStats;
    
    if (availableAreas.length === 0) {
      // Fallback if all areas are excluded
      return {
        area: areaStats[0].name,
        savePercentage: Math.round(100 - areaStats[0].goalPercentage),
        totalShots: areaStats[0].total,
        goals: areaStats[0].goals
      };
    }
    
    let bestArea = availableAreas[0];
    for (const area of availableAreas) {
      if (area.goalPercentage < bestArea.goalPercentage) {
        bestArea = area;
      }
    }

      return {
        area: bestArea.name,
        savePercentage: Math.round(100 - bestArea.goalPercentage),
        totalShots: bestArea.total,
        goals: bestArea.goals
      };
    } catch (error) {
      console.error('Error calculating top strength:', error);
      return {
        area: 'Unknown',
        savePercentage: 0,
        totalShots: 0,
        goals: 0
      };
    }
  }

  private static calculateTopWeakness(goals: Goal[]) {
    try {
      const areaStats = this.calculateAreaStats(goals);
      
      if (areaStats.length === 0) {
        return {
          area: 'Bottom Right',
          goalPercentage: 0,
          totalShots: 0,
          goals: 0
        };
      }
    
    // Find area with highest goal percentage
    let worstArea = areaStats[0];
    for (const area of areaStats) {
      if (area.goalPercentage > worstArea.goalPercentage) {
        worstArea = area;
      }
    }

      return {
        area: worstArea.name,
        goalPercentage: Math.round(worstArea.goalPercentage),
        totalShots: worstArea.total,
        goals: worstArea.goals
      };
    } catch (error) {
      console.error('Error calculating top weakness:', error);
      return {
        area: 'Unknown',
        goalPercentage: 0,
        totalShots: 0,
        goals: 0
      };
    }
  }

  private static calculateMostRiskySituation(goals: Goal[]) {
    try {
      const situationStats: { [key: string]: { goals: number; total: number } } = {};

      // Count goals by game situation
      goals.forEach(goal => {
        const situation = goal.gameSituation || 'General Play'; // Default if missing
        if (!situationStats[situation]) {
          situationStats[situation] = { goals: 0, total: goals.length };
        }
        situationStats[situation].goals++;
      });

      // Find situation with highest proportion of total goals
      let riskiestSituation = { situation: 'General Play', goalPercentage: 0, totalShots: goals.length, goals: 0 };
      
      Object.entries(situationStats).forEach(([situation, stats]) => {
        // Calculate percentage of total goals from this situation
        const goalPercentage = goals.length > 0 ? (stats.goals / goals.length) * 100 : 0;
        if (stats.goals > riskiestSituation.goals) {
          riskiestSituation = {
            situation,
            goalPercentage: Math.round(goalPercentage),
            totalShots: goals.length,
            goals: stats.goals
          };
        }
      });

      return riskiestSituation;
    } catch (error) {
      console.error('Error calculating risky situation:', error);
      return { situation: 'Unknown', goalPercentage: 0, totalShots: 0, goals: 0 };
    }
  }

  private static calculateMostRiskyShotType(goals: Goal[]) {
    try {
      const shotTypeStats: { [key: string]: { goals: number; total: number } } = {};

      // Count goals by shot type
      goals.forEach(goal => {
        const shotType = goal.shotType || 'Wrist Shot'; // Default if missing
        if (!shotTypeStats[shotType]) {
          shotTypeStats[shotType] = { goals: 0, total: goals.length };
        }
        shotTypeStats[shotType].goals++;
      });

      // Find shot type with highest proportion of total goals
      let riskiestShotType = { shotType: 'Wrist Shot', goalPercentage: 0, totalShots: goals.length, goals: 0 };
      
      Object.entries(shotTypeStats).forEach(([shotType, stats]) => {
        // Calculate percentage of total goals from this shot type
        const goalPercentage = goals.length > 0 ? (stats.goals / goals.length) * 100 : 0;
        if (stats.goals > riskiestShotType.goals) {
          riskiestShotType = {
            shotType,
            goalPercentage: Math.round(goalPercentage),
            totalShots: goals.length,
            goals: stats.goals
          };
        }
      });

      return riskiestShotType;
    } catch (error) {
      console.error('Error calculating risky shot type:', error);
      return { shotType: 'Unknown', goalPercentage: 0, totalShots: 0, goals: 0 };
    }
  }

  private static calculateAreaStats(goals: Goal[]) {
    try {
      // Initialize area stats for each quadrant
      const areaStats = GOAL_AREAS.map(area => ({
        name: area.name,
        goals: 0,
        total: 0,
        goalPercentage: 0
      }));

      // Count goals in each 4x4 grid zone first (16 zones)
      const zoneCount = new Array(16).fill(0);
      goals.forEach(goal => {
        if (typeof goal.xPosition === 'number' && typeof goal.yPosition === 'number') {
          const zoneIndex = getZoneIndex(goal.xPosition, goal.yPosition);
          if (zoneIndex >= 0 && zoneIndex < 16) {
            zoneCount[zoneIndex]++;
          }
        } else {
          // If no position data, distribute evenly across zones
          const randomZoneIndex = Math.floor(Math.random() * 16);
          zoneCount[randomZoneIndex]++;
        }
      });

      // Aggregate zone counts into quadrants
      GOAL_AREAS.forEach((area, areaIndex) => {
        let totalGoalsInArea = 0;
        area.zones.forEach(zoneIndex => {
          totalGoalsInArea += zoneCount[zoneIndex];
        });
        
        // Since we only have goal data, calculate percentage of total goals in each area
        areaStats[areaIndex].goals = totalGoalsInArea;
        areaStats[areaIndex].total = goals.length; // Total goals across all areas
        
        // Calculate percentage based on distribution across areas
        const totalGoals = goals.length;
        areaStats[areaIndex].goalPercentage = totalGoals > 0 ? (totalGoalsInArea / totalGoals) * 100 : 0;
      });

      return areaStats;
    } catch (error) {
      console.error('Error calculating area stats:', error);
      return [];
    }
  }
}